import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ObjMesh } from "../../utils/objImport";
import { renderMesh, type MeshRotation } from "./meshRenderer";
import "./ModelMeshRenderer.css";

interface ModelMeshRendererProps {
  /** Mesh in build space (obj x → build x, obj z → build y, obj y → build z) */
  mesh: ObjMesh;
  canvasSize: { width: number; height: number };
  /** screen pixels per grid unit — shared with the brick build */
  pixelSize: number;
  rotation: MeshRotation;
  scale: number;
  color: string;
}

/**
 * The original model, rendered as a flat-shaded mesh. Same canvas chrome and
 * camera as the brick view, so toggling between the two reads as one object
 * changing form.
 */
const ModelMeshRenderer: React.FC<ModelMeshRendererProps> = ({
  mesh,
  canvasSize,
  pixelSize,
  rotation,
  scale,
  color,
}) => {
  const { t } = useTranslation();
  const renderCanvas = useMemo(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    // Background (warm subtle, per .impeccable.md) — same as the brick view
    ctx.fillStyle = "#f8f5f0";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    renderMesh(ctx, mesh, { canvasSize, pixelSize, rotation, scale, color });

    return canvas;
  }, [mesh, canvasSize, pixelSize, rotation, scale, color]);

  if (!renderCanvas) return null;

  return (
    <div className="model-mesh-renderer">
      <canvas
        className="model-mesh-canvas"
        role="img"
        aria-label={t("model.previewHint")}
        width={canvasSize.width}
        height={canvasSize.height}
        ref={(canvas) => {
          if (canvas && renderCanvas) {
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(renderCanvas, 0, 0);
            }
          }
        }}
      />
    </div>
  );
};

export default React.memo(ModelMeshRenderer);
